/**
 * Trades Poster — Licences, Badges & Tickets
 * Source catalogue of Licence / Badge / Ticket items, Source IDs and
 * item-specific application-input definitions linked to Form Inventory.
 */

/** Item Type values owned by this register. */
export const LBT_ITEM_TYPE_OPTIONS = [
  { value: 'licence', label: 'Licence' },
  { value: 'badge', label: 'Badge' },
  { value: 'trade_ticket', label: 'Ticket' },
];

export const LBT_ITEM_TYPE_SORT_ORDER = {
  licence: 0,
  badge: 1,
  trade_ticket: 2,
};

/** Item ID prefixes by type (Licence / Badge / Ticket). */
export const LBT_ITEM_ID_PREFIX = {
  licence: 'LIC',
  badge: 'BDG',
  trade_ticket: 'TKT',
};

/** Application status values (controlled by submission, Compliance, expiry or withdrawal). */
export const LBT_STATUS_OPTIONS = [
  'Pending',
  'Resubmission Required',
  'Approved',
  'Approved — Renewal Pending',
  'Rejected',
  'Expired',
  'Withdrawn',
];

export const LBT_PROFILE_ICON_OPTIONS = ['Visible', 'Hidden'];

export const LBT_APPLICATION_BASIS_OPTIONS = [
  'New Application',
  'Renewal',
  'Reapplication',
];

/** Australian states and territories — used when a Business adds a licence. */
export const LBT_JURISDICTION_OPTIONS = [
  'Australian Capital Territory',
  'New South Wales',
  'Northern Territory',
  'Queensland',
  'South Australia',
  'Tasmania',
  'Victoria',
  'Western Australia',
];

const SOURCE_ACTOR = 'Saim Sahi';

const sourceStamp = (hour, minute) =>
  new Date(2026, 5, 29, hour, minute, 0, 0).toISOString();

/** Default Form Inventory form name linked by LBT Type when a seed has no linkedFormName. */
export const LBT_DEFAULT_FORM_NAME_BY_TYPE = {
  licence: 'Licence Form',
  badge: 'Badge First Aid Form',
  trade_ticket: 'Ticket Form',
};

/** Per-source Form Inventory form from the LBT field stacks. */
export const LBT_FORM_NAME_BY_ITEM_ID = {
  'LIC-0001': 'Licence Form',
  'LIC-0002': 'Licence Form',
  'LIC-0003': 'Licence Form',
  'BDG-0001': 'Badge First Aid Form',
  'BDG-0002': 'Badge First Aid Form',
  'BDG-0003': 'Badge Working with Children Check Form',
  'BDG-0004': 'Badge Insurance Form',
  'TKT-0001': 'Ticket Form',
  'TKT-0002': 'Ticket Form',
  'TKT-0003': 'Ticket Form',
};

export const LBT_FIELD_CONTROL_OPTIONS = [
  'Single-line text',
  'Multi-line text',
  'Single-select',
  'Multi-select',
  'Date',
  'File upload',
];

export const LBT_FIELD_REQUIRED_OPTIONS = ['Required', 'Optional'];

const formInput = (fieldLabel, fieldControl, required) => ({
  id: `fld-${String(fieldLabel)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`,
  fieldLabel,
  fieldControl,
  required,
});

export function createEmptyLbtFormInput() {
  return {
    id: `fld-${Math.random().toString(36).slice(2, 10)}`,
    fieldLabel: '',
    fieldControl: '',
    required: '',
  };
}

export function cloneLbtFormInputs(inputs = []) {
  return (inputs || []).map((input) => ({
    id: input.id || createEmptyLbtFormInput().id,
    fieldLabel: input.fieldLabel || '',
    fieldControl: input.fieldControl || '',
    required: input.required || '',
  }));
}

export const LBT_FORM_INPUT_TEMPLATES = {
  licence: [
    formInput('Holder Name', 'Single-line text', 'Required'),
    formInput('Licence Number', 'Single-line text', 'Required'),
    formInput('Issuing Authority', 'Single-select', 'Required'),
    formInput('Jurisdiction', 'Single-select', 'Required'),
    formInput('Licence Class', 'Multi-select', 'Required'),
    formInput('Licence Category', 'Single-select', 'Required'),
    formInput('Issue Date', 'Date', 'Required'),
    formInput('Expiry Date', 'Date', 'Required'),
    formInput('Evidence', 'File upload', 'Required'),
    formInput('Endorsements', 'Single-line text', 'Optional'),
    formInput('Licence Conditions / Restrictions', 'Multi-line text', 'Optional'),
    formInput('Scope of Work', 'Single-line text', 'Optional'),
    formInput('Licence Variation', 'Single-line text', 'Optional'),
  ],
  firstAid: [
    formInput('Holder Name', 'Single-line text', 'Required'),
    formInput('Certificate / Reference Number', 'Single-line text', 'Optional'),
    formInput('Issuing Organisation', 'Single-line text', 'Required'),
    formInput('Course / Unit Name', 'Single-line text', 'Optional'),
    formInput('Course / Unit Code', 'Single-line text', 'Optional'),
    formInput('Issue Date', 'Date', 'Required'),
    formInput('Expiry Date', 'Date', 'Optional'),
    formInput('Evidence', 'File upload', 'Required'),
  ],
  insurance: [
    formInput('Policyholder / Insured Name', 'Single-line text', 'Required'),
    formInput('Insurer', 'Single-line text', 'Required'),
    formInput('Policy Number', 'Single-line text', 'Required'),
    formInput('Cover Start Date', 'Date', 'Required'),
    formInput('Cover End Date', 'Date', 'Required'),
    formInput('Cover Details', 'Multi-line text', 'Optional'),
    formInput('Cover Amount', 'Single-line text', 'Optional'),
    formInput('Evidence', 'File upload', 'Required'),
  ],
  wwcc: [
    formInput('Holder Name', 'Single-line text', 'Required'),
    formInput('Check / Reference Number', 'Single-line text', 'Optional'),
    formInput('Issuing Organisation', 'Single-line text', 'Required'),
    formInput('Jurisdiction', 'Single-select', 'Optional'),
    formInput('Issue Date', 'Date', 'Required'),
    formInput('Expiry Date', 'Date', 'Optional'),
    formInput('Evidence', 'File upload', 'Required'),
  ],
  ticket: [
    formInput('Holder Name', 'Single-line text', 'Required'),
    formInput('Ticket / Certificate / Reference Number', 'Single-line text', 'Required'),
    formInput('Issuing Organisation', 'Single-line text', 'Required'),
    formInput('Training Provider / RTO', 'Single-line text', 'Optional'),
    formInput('RTO Code', 'Single-line text', 'Optional'),
    formInput('Jurisdiction', 'Single-select', 'Optional'),
    formInput('Course / Unit Name(s)', 'Multi-line text', 'Optional'),
    formInput('Course / Unit Code(s)', 'Multi-line text', 'Optional'),
    formInput('Plant / Equipment Type', 'Single-line text', 'Optional'),
    formInput('Class / Level', 'Single-line text', 'Optional'),
    formInput('Conditions / Restrictions', 'Multi-line text', 'Optional'),
    formInput('Issue Date', 'Date', 'Required'),
    formInput('Expiry Date', 'Date', 'Optional'),
    formInput('Evidence', 'File upload', 'Required'),
  ],
};

export function defaultFormInputsForType(itemType) {
  if (itemType === 'licence') return cloneLbtFormInputs(LBT_FORM_INPUT_TEMPLATES.licence);
  if (itemType === 'badge') return cloneLbtFormInputs(LBT_FORM_INPUT_TEMPLATES.firstAid);
  if (itemType === 'trade_ticket') return cloneLbtFormInputs(LBT_FORM_INPUT_TEMPLATES.ticket);
  return [];
}

export function defaultFormInputsForItem(itemName, itemType) {
  const name = String(itemName || '').trim().toLowerCase();
  if (name.includes('insurance')) return cloneLbtFormInputs(LBT_FORM_INPUT_TEMPLATES.insurance);
  if (name.includes('children') || name.includes('police')) {
    return cloneLbtFormInputs(LBT_FORM_INPUT_TEMPLATES.wwcc);
  }
  if (name.includes('first aid')) return cloneLbtFormInputs(LBT_FORM_INPUT_TEMPLATES.firstAid);
  return defaultFormInputsForType(itemType);
}

export function isLbtFormInputComplete(input) {
  return Boolean(
    String(input?.fieldLabel || '').trim() &&
      LBT_FIELD_CONTROL_OPTIONS.includes(input?.fieldControl) &&
      LBT_FIELD_REQUIRED_OPTIONS.includes(input?.required),
  );
}

export function findFormInventoryFormByName(forms = [], formName = '') {
  const target = String(formName || '').trim().toLowerCase();
  if (!target) return null;
  return (forms || []).find((form) => String(form.formName || '').trim().toLowerCase() === target) || null;
}

export function resolveFormIdForLbtItem(item, forms = []) {
  if (!item) return '';
  const byId = LBT_FORM_NAME_BY_ITEM_ID[item.id];
  const byType = LBT_DEFAULT_FORM_NAME_BY_TYPE[item.itemType];
  const formName = item.linkedFormName || byId || byType || '';
  return findFormInventoryFormByName(forms, formName)?.id || '';
}

export function resolveDefaultFormIdForLbtType(itemType, forms = []) {
  const formName = LBT_DEFAULT_FORM_NAME_BY_TYPE[itemType];
  if (!formName) return '';
  return findFormInventoryFormByName(forms, formName)?.id || '';
}

/**
 * Source catalogue owned by this register.
 * Configuration references these without duplicating them.
 * Item IDs use type prefixes: LIC- / BDG- / TKT-.
 * formInputs are the item’s application fields; Form Inventory stores the skeleton.
 */
export const LBT_ALL_TRADES_CATEGORY_ID = 'ALL_TRADES';

const seedItem = ({
  id,
  itemType,
  itemName,
  description,
  tradeCategoryName,
  tradeCategoryId,
  iconFileName,
  hour,
  minute,
}) => ({
  id,
  itemType,
  itemName,
  description,
  itemStatus: 'Active',
  ...(tradeCategoryId ? { tradeCategoryId } : {}),
  ...(tradeCategoryName ? { tradeCategoryName } : {}),
  iconUrl: '',
  iconFileName,
  iconLabel: `${itemName} icon`,
  formInputs: defaultFormInputsForItem(itemName, itemType),
  createdOn: sourceStamp(hour, minute),
  createdBy: SOURCE_ACTOR,
  lastUpdatedOn: sourceStamp(hour, minute),
  lastUpdatedBy: SOURCE_ACTOR,
});

const LBT_SOURCE_ITEM_SEEDS = [
  seedItem({
    id: 'LIC-0001',
    itemType: 'licence',
    itemName: 'Electrical Licence',
    description: 'Electrical contractor licence details.',
    tradeCategoryName: 'Electrical',
    iconFileName: 'electrical-licence.svg',
    hour: 9,
    minute: 0,
  }),
  seedItem({
    id: 'LIC-0002',
    itemType: 'licence',
    itemName: 'Plumbing Licence',
    description: 'State-issued licence to carry out plumbing work.',
    tradeCategoryName: 'Plumbing',
    iconFileName: 'plumbing-licence.svg',
    hour: 9,
    minute: 10,
  }),
  seedItem({
    id: 'BDG-0001',
    itemType: 'badge',
    itemName: 'First Aid',
    description: 'First aid details and supporting evidence.',
    tradeCategoryId: LBT_ALL_TRADES_CATEGORY_ID,
    iconFileName: 'first-aid.svg',
    hour: 9,
    minute: 20,
  }),
  seedItem({
    id: 'BDG-0002',
    itemType: 'badge',
    itemName: 'Industry Membership',
    description: 'Industry membership details.',
    tradeCategoryId: LBT_ALL_TRADES_CATEGORY_ID,
    iconFileName: 'industry-membership.svg',
    hour: 9,
    minute: 30,
  }),
  seedItem({
    id: 'BDG-0003',
    itemType: 'badge',
    itemName: 'Working with Children Check',
    description: 'Working with Children Check details.',
    tradeCategoryId: LBT_ALL_TRADES_CATEGORY_ID,
    iconFileName: 'wwcc.svg',
    hour: 9,
    minute: 40,
  }),
  seedItem({
    id: 'TKT-0001',
    itemType: 'trade_ticket',
    itemName: 'White Card',
    description: 'White Card details and supporting evidence.',
    tradeCategoryId: LBT_ALL_TRADES_CATEGORY_ID,
    iconFileName: 'white-card.svg',
    hour: 9,
    minute: 50,
  }),
  seedItem({
    id: 'TKT-0002',
    itemType: 'trade_ticket',
    itemName: 'Working at Heights',
    description: 'Working at Heights details.',
    tradeCategoryId: LBT_ALL_TRADES_CATEGORY_ID,
    iconFileName: 'working-at-heights.svg',
    hour: 10,
    minute: 0,
  }),
  seedItem({
    id: 'BDG-0004',
    itemType: 'badge',
    itemName: 'Insurance',
    description: 'Public liability insurance policy details.',
    tradeCategoryId: LBT_ALL_TRADES_CATEGORY_ID,
    iconFileName: 'insurance.svg',
    hour: 10,
    minute: 10,
  }),
  seedItem({
    id: 'LIC-0003',
    itemType: 'licence',
    itemName: 'Gas Fitting Licence',
    description: 'State-issued licence to carry out gas fitting work.',
    tradeCategoryName: 'Plumbing',
    iconFileName: 'gas-fitting-licence.svg',
    hour: 10,
    minute: 20,
  }),
  seedItem({
    id: 'TKT-0003',
    itemType: 'trade_ticket',
    itemName: 'Confined Spaces',
    description: 'Confined spaces ticket details.',
    tradeCategoryId: LBT_ALL_TRADES_CATEGORY_ID,
    iconFileName: 'confined-spaces.svg',
    hour: 10,
    minute: 30,
  }),
];

export function findTradeCategoryIdByName(tradeCategories = [], name = '') {
  const target = String(name || '').trim().toLowerCase();
  if (!target) return '';
  return (
    (tradeCategories || []).find(
      (row) => String(row.name || '').trim().toLowerCase() === target,
    )?.id || ''
  );
}

export function resolveTradeCategoryIdForLbtItem(item, tradeCategories = []) {
  if (!item) return '';
  if (item.tradeCategoryId === LBT_ALL_TRADES_CATEGORY_ID) return LBT_ALL_TRADES_CATEGORY_ID;
  if (item.tradeCategoryId) {
    const exists = (tradeCategories || []).some((row) => row.id === item.tradeCategoryId);
    if (exists) return item.tradeCategoryId;
  }
  if (item.tradeCategoryName) {
    return findTradeCategoryIdByName(tradeCategories, item.tradeCategoryName);
  }
  return '';
}

/** Resolved catalogue with formId filled from Form Inventory defaults by Type. */
export const LBT_SOURCE_ITEMS = LBT_SOURCE_ITEM_SEEDS.map((item) => ({
  ...item,
  formId: '',
  tradeCategoryId: item.tradeCategoryId || '',
}));

export function buildLbtSourceCatalog(forms = [], tradeCategories = []) {
  return LBT_SOURCE_ITEM_SEEDS.map((item) => ({
    ...item,
    formId: resolveFormIdForLbtItem(item, forms),
    tradeCategoryId: resolveTradeCategoryIdForLbtItem(item, tradeCategories),
    formInputs: cloneLbtFormInputs(item.formInputs || defaultFormInputsForItem(item.itemName, item.itemType)),
  }));
}

export function emptyLbtSourceItem() {
  return {
    id: '',
    itemType: '',
    itemName: '',
    description: '',
    itemStatus: 'Active',
    formId: '',
    tradeCategoryId: '',
    iconUrl: '',
    iconFileName: '',
    iconLabel: '',
    formInputs: [],
    createdOn: '',
    createdBy: '',
    lastUpdatedOn: '',
    lastUpdatedBy: '',
  };
}

export function cloneLbtSourceItem(record) {
  return {
    ...emptyLbtSourceItem(),
    ...record,
    formInputs: cloneLbtFormInputs(record?.formInputs),
  };
}

/** Fictional Business accounts for search-and-select (sample only). */
export const LBT_BUSINESS_OPTIONS = [
  {
    businessId: 'BUS-00418',
    businessName: 'Bright Spark Electrical Pty Ltd',
    businessDisplay: 'Bright Spark Electrical',
    abn: '12 345 678 901',
  },
  {
    businessId: 'BUS-00426',
    businessName: 'Metro Build Group Pty Ltd',
    businessDisplay: 'Metro Build Group',
    abn: '45 678 901 234',
  },
  {
    businessId: 'BUS-00431',
    businessName: 'Apex Roofing Services Pty Ltd',
    businessDisplay: 'Apex Roofing Services',
    abn: '78 901 234 567',
  },
  {
    businessId: 'BUS-00439',
    businessName: 'SafeFlow Plumbing Pty Ltd',
    businessDisplay: 'SafeFlow Plumbing',
    abn: '23 456 789 012',
  },
];

/**
 * Screen 1 sample records (all values fictional per spec).
 */
export const LBT_SAMPLE_RECORDS = [
  {
    id: 'LBT-001842',
    businessId: 'BUS-00418',
    businessName: 'Bright Spark Electrical Pty Ltd',
    businessDisplay: 'Bright Spark Electrical',
    abn: '12 345 678 901',
    itemType: 'licence',
    itemName: 'Electrical Licence',
    sourceItemId: 'LIC-0001',
    selectedIcon: 'Electrical Licence icon',
    applicationBasis: 'New Application',
    credentialHolder: 'Bright Spark Electrical Pty Ltd',
    issuingAuthority: 'NSW Fair Trading',
    jurisdiction: 'New South Wales',
    credentialNumber: 'EC-482917',
    issueDate: '2024-07-01',
    hasExpiry: 'Yes',
    expiryDate: '2027-06-30',
    submittedEvidence: 'electrical-contractor-licence.pdf',
    status: 'Approved',
    profileIcon: 'Visible',
    complianceCaseId: 'CMP-007341',
    decisionId: 'DEC-003921',
    submittedOn: '2026-07-21T10:42:00',
    lastUpdated: '2026-07-24T15:18:00',
  },
  {
    id: 'LBT-001843',
    businessId: 'BUS-00426',
    businessName: 'Metro Build Group Pty Ltd',
    businessDisplay: 'Metro Build Group',
    abn: '45 678 901 234',
    itemType: 'trade_ticket',
    itemName: 'White Card',
    sourceItemId: 'TKT-0001',
    selectedIcon: 'White Card icon',
    applicationBasis: 'New Application',
    credentialHolder: 'Metro Build Group Pty Ltd',
    issuingAuthority: 'Registered Training Organisation',
    jurisdiction: 'Victoria',
    credentialNumber: 'WC-930184',
    issueDate: '2025-11-12',
    hasExpiry: 'No',
    expiryDate: '',
    submittedEvidence: 'white-card-metro-build.pdf',
    status: 'Pending',
    profileIcon: 'Hidden',
    complianceCaseId: 'CMP-007349',
    decisionId: '',
    submittedOn: '2026-08-02T09:15:00',
    lastUpdated: '2026-08-02T09:15:00',
  },
  {
    id: 'LBT-001844',
    businessId: 'BUS-00431',
    businessName: 'Apex Roofing Services Pty Ltd',
    businessDisplay: 'Apex Roofing Services',
    abn: '78 901 234 567',
    itemType: 'badge',
    itemName: 'Industry Membership',
    sourceItemId: 'BDG-0002',
    selectedIcon: 'Industry Membership icon',
    applicationBasis: 'New Application',
    credentialHolder: 'Apex Roofing Services Pty Ltd',
    issuingAuthority: 'Industry Association',
    jurisdiction: 'Queensland',
    credentialNumber: 'IM-228104',
    issueDate: '2024-07-18',
    hasExpiry: 'Yes',
    expiryDate: '2026-07-18',
    submittedEvidence: 'industry-membership-apex.pdf',
    status: 'Expired',
    profileIcon: 'Hidden',
    complianceCaseId: 'CMP-007356',
    decisionId: 'DEC-003940',
    submittedOn: '2025-06-20T11:00:00',
    lastUpdated: '2026-07-18T00:00:00',
  },
  {
    id: 'LBT-001845',
    businessId: 'BUS-00439',
    businessName: 'SafeFlow Plumbing Pty Ltd',
    businessDisplay: 'SafeFlow Plumbing',
    abn: '23 456 789 012',
    itemType: 'trade_ticket',
    itemName: 'Working at Heights',
    sourceItemId: 'TKT-0002',
    selectedIcon: 'Working at Heights icon',
    applicationBasis: 'New Application',
    credentialHolder: 'SafeFlow Plumbing Pty Ltd',
    issuingAuthority: 'Registered Training Organisation',
    jurisdiction: 'New South Wales',
    credentialNumber: 'WH-551920',
    issueDate: '2025-03-12',
    hasExpiry: 'Yes',
    expiryDate: '2027-03-12',
    submittedEvidence: 'working-at-heights-safeflow.pdf',
    status: 'Withdrawn',
    profileIcon: 'Hidden',
    complianceCaseId: 'CMP-007362',
    decisionId: 'DEC-003955',
    submittedOn: '2026-07-10T14:22:00',
    lastUpdated: '2026-08-03T16:40:00',
  },
];

export function buildLicensingCredentialRecords() {
  return LBT_SAMPLE_RECORDS.map((row) => ({ ...row }));
}

export function getLbtItemTypeLabel(value) {
  const found = LBT_ITEM_TYPE_OPTIONS.find((o) => o.value === value);
  return found ? found.label : value || '—';
}

export function getSourceItemsForType(itemType) {
  if (!itemType) return [];
  return LBT_SOURCE_ITEMS.filter((item) => item.itemType === itemType);
}

export function findSourceItem(itemName) {
  if (!itemName) return null;
  return LBT_SOURCE_ITEMS.find((item) => item.itemName === itemName) || null;
}

export function findBusinessOption(businessId) {
  if (!businessId) return null;
  return LBT_BUSINESS_OPTIONS.find((b) => b.businessId === businessId) || null;
}

export function emptyLicensingCredentialRecord() {
  return {
    id: '',
    businessId: '',
    businessName: '',
    businessDisplay: '',
    abn: '',
    itemType: '',
    itemName: '',
    sourceItemId: '',
    selectedIcon: '',
    applicationBasis: 'New Application',
    credentialHolder: '',
    issuingAuthority: '',
    jurisdiction: '',
    credentialNumber: '',
    issueDate: '',
    hasExpiry: '',
    expiryDate: '',
    submittedEvidence: '',
    status: 'Pending',
    profileIcon: 'Hidden',
    complianceCaseId: '',
    decisionId: '',
    submittedOn: '',
    lastUpdated: '',
  };
}

export function cloneLicensingCredentialRecord(record) {
  return {
    ...emptyLicensingCredentialRecord(),
    ...record,
  };
}
